<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import { useEditions } from '@/composables/useEditions'
import { useRaces } from '@/composables/useRaces'
import RaceForm from '@/components/organizer/events/RaceForm.vue'
import RacePriceTiersSection from '@/components/organizer/events/RacePriceTiersSection.vue'
import { DEFAULT_TIMEZONE } from '@/constants/timezones'
import type { StoreRacePayload } from '@/types/events'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import PayoutNotReadyNotice from '@/components/organizer/payouts/PayoutNotReadyNotice.vue'
import { payoutNotReadyMessage } from '@/api/payouts'

interface RaceFormData {
  name: string
  type: '' | 'road' | 'trail' | 'track' | 'duathlon' | 'triathlon' | 'cycling' | 'virtual' | 'other'
  base_price: string
  distance: string
  elevation: string
  start_venue_id: number | null
  end_venue_id: number | null
  start_time: string
  cutoff: string
  max_participants: string
  allow_waiting_list: boolean
  payment_deadline_hours: string
  registrations_closing_time: string
  registrations_open: boolean
  sort_order: string
}

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

// Convert 'YYYY-MM-DD HH:mm:ss' to 'YYYY-MM-DDTHH:mm' for datetime-local input
function toDatetimeLocal(value: string | null): string {
  if (!value) return ''
  return value.slice(0, 16).replace(' ', 'T')
}

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { event, fetchEvent } = useEvents()
const { edition, fetchEdition } = useEditions()
const { race, loading, fetchRace, updateRace } = useRaces()

const formLoading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const payoutNotReady = ref<string | null>(null)

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string
const raceSlug = route.params['raceSlug'] as string

const initialStartTimezone = computed<string | undefined>(() => {
  if (race.value === null) return undefined
  // start_venue is already the resolved venue (own or inherited from the edition).
  return race.value.start_venue?.timezone ?? edition.value?.venue?.timezone ?? DEFAULT_TIMEZONE
})

// The venue pickers are organisation-scoped and a race is one hop deeper than
// an edition (race -> edition -> event -> organisation). The event's own
// organisation_id is the authoritative source; the auth store is not consulted.
const organisationId = computed<number | null>(() => event.value?.organisation_id ?? null)

const form = ref<RaceFormData>({
  name: '',
  type: '',
  base_price: '',
  distance: '',
  elevation: '',
  start_venue_id: null,
  end_venue_id: null,
  start_time: '',
  cutoff: '',
  max_participants: '',
  allow_waiting_list: false,
  payment_deadline_hours: '',
  registrations_closing_time: '',
  registrations_open: false,
  sort_order: '0',
})

onMounted(async () => {
  await Promise.all([
    fetchEvent(eventSlug),
    fetchEdition(eventSlug, editionSlug),
    fetchRace(eventSlug, editionSlug, raceSlug),
  ])
  if (race.value) {
    form.value = {
      name: race.value.name,
      type: race.value.type,
      base_price: race.value.base_price,
      distance: race.value.distance,
      elevation: race.value.elevation !== null ? String(race.value.elevation) : '',
      // Prefilled from the raw foreign keys, never from the resolved
      // `start_venue`: that is the edition's venue when the race has none, so
      // using it would silently convert an inheriting race into one carrying
      // its own copy of the edition venue on the next save.
      start_venue_id: race.value.start_venue_id,
      end_venue_id: race.value.end_venue_id,
      start_time: toDatetimeLocal(race.value.start_time),
      cutoff: race.value.cutoff !== null ? String(race.value.cutoff) : '',
      max_participants: race.value.max_participants !== null ? String(race.value.max_participants) : '',
      allow_waiting_list: race.value.allow_waiting_list,
      payment_deadline_hours: race.value.payment_deadline_hours !== null ? String(race.value.payment_deadline_hours) : '',
      registrations_closing_time: toDatetimeLocal(race.value.registrations_closing_time),
      registrations_open: race.value.registrations_open,
      sort_order: String(race.value.sort_order),
    }
  }
})

async function handleSubmit(data: StoreRacePayload): Promise<void> {
  errors.value = {}
  generalError.value = ''
  payoutNotReady.value = null
  formLoading.value = true
  try {
    await updateRace(eventSlug, editionSlug, raceSlug, data)
    void router.push({ name: 'organizer.races.edit', params: { eventSlug, editionSlug, raceSlug } })
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      const notReady = payoutNotReadyMessage(e)
      if (notReady !== null) {
        payoutNotReady.value = notReady
      } else if (e.response.status === 422 && e.response.data.errors) {
        errors.value = e.response.data.errors
      } else {
        generalError.value = t('races.form.error_generic')
      }
    } else {
      generalError.value = t('races.form.error_generic')
    }
  } finally {
    formLoading.value = false
  }
}
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}/editions/${editionSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="edition">{{ edition.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${raceSlug}/participants`" class="hover:text-slate-300 transition-colors">
          <template v-if="race">{{ race.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">{{ t('nav.sidebar.settings') }}</span>
      </nav>

      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white">{{ event?.name ?? '…' }} · {{ race?.name ?? '…' }}</h1>
        <p class="text-slate-400 mt-1">{{ t('races.form.edit_heading') }}</p>
      </div>

      <AppSpinner v-if="loading || organisationId === null" />

      <!-- Centred form stack (max-w-3xl, same as the create page); the header above keeps the wide layout -->
      <div v-else class="max-w-3xl mx-auto">
        <PayoutNotReadyNotice v-if="payoutNotReady" :message="payoutNotReady" />

        <div
          v-if="generalError"
          class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
        >
          {{ generalError }}
        </div>

        <RaceForm
          v-model="form"
          :loading="formLoading"
          :errors="errors"
          :organisation-id="organisationId"
          :edition-venue-timezone="edition?.venue?.timezone ?? null"
          :initial-start-timezone="initialStartTimezone"
          :edition-venue="edition?.venue ?? null"
          :initial-start-venue="race?.start_venue ?? null"
          :is-edit="true"
          @submit="handleSubmit"
          @cancel="router.push(`/organizer/events/${eventSlug}/editions/${editionSlug}`)"
        >
          <template #price-tiers="{ timezone }">
            <RacePriceTiersSection
              mode="persisted"
              :organisation-id="organisationId"
              :timezone="timezone"
              :standard-price="form.base_price"
              :event-slug="eventSlug"
              :edition-slug="editionSlug"
              :race-slug="raceSlug"
              :initial-tiers="race?.price_tiers"
              :initial-pricing="race?.pricing"
            />
          </template>
        </RaceForm>
      </div>

    </main>
  </OrganizerLayout>
</template>
